import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Plus, Loader2, Sparkles, Eye, EyeOff, Trash2, Globe,
  Upload, ChevronDown, ArrowLeft, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { setWallpapers, getAllWallpapers } from '@/lib/collections/wallpapers';
import { Time, Address } from '@/lib/db-client';
import { ADMIN_ADDRESS } from '@/lib/constants';
import { useAuth, getIdToken } from '@pooflabs/web';
import { AuthContextType } from '@/components/types';
import { getPoofAPIUrl, getPoofAPIHeaders } from '@/lib/config';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { AdminBulkUpload } from '@/components/AdminBulkUpload';
import { AdminGenerateWallpaper } from '@/components/AdminGenerateWallpaper';
import { AdminFetchWallpapers } from '@/components/AdminFetchWallpapers';
import { loadApiKey, saveApiKey, deleteApiKey, analyzeWallpaperImage, fileToBase64DataUrl } from '@/utils/adminApiKey';
import { haptic } from '@/utils/haptic';
import { CategoryNewButton } from '@/components/CategoryNewButton';
import {
  ContentType,
  CONTENT_TYPE_BADGE,
  ALL_ACCEPT_ATTR,
  MEDIA_TYPE_MAP,
  BASE_LIVE_CATEGORIES,
  RINGTONE_CATEGORIES,
  detectContentType,
} from '@/utils/contentTypeDetect';
import { useWallpaperCategoriesFromDB } from '@/utils/wallpaperCategories';
import { processWallpaperImage } from '@/utils/processWallpaperImage';

// The existing MediaType used for the AI/Library section (wallpaper-only) and AdminBulkUpload
type MediaType = 'wallpaper' | 'live-wallpaper' | 'ringtone' | 'icon';

// Per-file upload item state
type UploadItemStatus = 'pending' | 'uploading' | 'done' | 'error' | 'duplicate';

interface UploadItem {
  id: string;
  file: File;
  localPreview: string;
  detectedType: ContentType;
  title: string;
  category: string;
  price: number | '';
  status: UploadItemStatus;
  error?: string;
  isAnalyzing?: boolean;
  aiAnalyzed?: boolean;
}

function filenameToTitle(filename: string): string {
  const base = filename.replace(/\.[^/.]+$/, '');
  return base.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();
}

interface AdminAddWallpaperProps {
  isOpen: boolean;
  onClose: () => void;
  existingCategories?: string[];
  existingLiveWallpaperCategories?: string[];
  existingRingtoneCategories?: string[];
  initialMediaType?: MediaType;
  onNewCategory?: (mediaType: 'wallpaper' | 'live-wallpaper' | 'ringtone', category: string) => void;
}

export const AdminAddWallpaper: React.FC<AdminAddWallpaperProps> = ({
  isOpen, onClose,
  existingCategories, existingLiveWallpaperCategories, existingRingtoneCategories,
  initialMediaType, onNewCategory,
}) => {
  const { user } = useAuth() as AuthContextType;

  // Live wallpaper categories from the admin-managed DB list.
  const { categories: dbWallpaperCategories } = useWallpaperCategoriesFromDB();

  // ── AI-section state (unchanged) ──────────────────────────────────────────
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiKeyLoaded, setApiKeyLoaded] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [deletingKey, setDeletingKey] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  // mediaType is now used only for the AI/Library section logic + AdminBulkUpload sub-modal
  const [mediaType, setMediaType] = useState<MediaType>(initialMediaType ?? 'wallpaper');

  // ── Upload queue state ────────────────────────────────────────────────────
  const [items, setItems] = useState<UploadItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [doneCount, setDoneCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [dupCount, setDupCount] = useState(0);
  const [customCategories, setCustomCategories] = useState<string[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isGlobalAnalyzing, setIsGlobalAnalyzing] = useState(false);

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && initialMediaType) setMediaType(initialMediaType);
  }, [isOpen, initialMediaType]);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    loadApiKey().then(key => {
      setApiKey(key);
      setApiKeyLoaded(true);
    });
  }, []);

  const saveKeyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleApiKeyChange = (newKey: string) => {
    setApiKey(newKey);
    if (saveKeyTimer.current) clearTimeout(saveKeyTimer.current);
    saveKeyTimer.current = setTimeout(() => {
      if (newKey.trim()) saveApiKey(newKey.trim());
    }, 800);
  };

  const handleDeleteApiKey = async () => {
    setDeletingKey(true);
    const ok = await deleteApiKey();
    if (ok) {
      setApiKey('');
    } else {
      toast.error('Failed to delete API key');
    }
    setDeletingKey(false);
  };

  function detectProvider(key: string): string {
    if (key.startsWith('r8_')) return 'Replicate';
    if (key.startsWith('sk_')) return 'fal.ai';
    if (key.startsWith('sk-proj-') || (key.startsWith('sk-') && key.length >= 48 && key.length <= 56)) return 'OpenAI';
    if (key.startsWith('sk-ant-')) return 'Anthropic';
    if (key.startsWith('sk-')) return 'Stability AI';
    return '';
  }

  const detectedProvider = apiKey.trim() ? detectProvider(apiKey.trim()) : '';

  // ── Category helpers ──────────────────────────────────────────────────────
  // Build category list for a given detected content type
  const getCategoriesForType = useCallback((ct: ContentType): string[] => {
    if (ct === 'live') {
      return [...new Set([...BASE_LIVE_CATEGORIES, ...(existingLiveWallpaperCategories ?? []), ...customCategories.filter(c => c)])].filter(c => c.toLowerCase() !== 'all');
    }
    if (ct === 'ringtone') {
      return [...new Set([...RINGTONE_CATEGORIES, ...(existingRingtoneCategories ?? []), ...customCategories.filter(c => c)])];
    }
    // wallpaper — use the admin-managed DB list exclusively (no merge with old DB-derived categories)
    return dbWallpaperCategories.filter(c => c.toLowerCase() !== 'all');
  }, [dbWallpaperCategories, existingLiveWallpaperCategories, existingRingtoneCategories, customCategories]);

  // Pick the first/best default category for a detected type
  const defaultCategoryForType = useCallback((ct: ContentType): string => {
    const cats = getCategoriesForType(ct);
    return cats[0] ?? '';
  }, [getCategoriesForType]);

  // ── File picker ───────────────────────────────────────────────────────────
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (files.length === 0) return;

    const rejected: string[] = [];
    const newItems: UploadItem[] = [];

    for (const file of files) {
      const ct = detectContentType(file);
      if (!ct) {
        rejected.push(file.name);
        continue;
      }
      newItems.push({
        id: `admin-add-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        localPreview: URL.createObjectURL(file),
        detectedType: ct,
        title: filenameToTitle(file.name),
        category: '',
        price: 1,
        status: 'pending',
      });
    }

    if (rejected.length > 0) {
      toast.error(`${rejected.length} file${rejected.length !== 1 ? 's' : ''} rejected — unsupported format.`);
    }

    setItems(prev => [...prev, ...newItems]);
  };

  const removeItem = (id: string) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item) try { URL.revokeObjectURL(item.localPreview); } catch { /* ignore */ }
      return prev.filter(i => i.id !== id);
    });
  };

  const updateItem = (id: string, patch: Partial<UploadItem>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  };

  // ── Global AI analyze all un-analyzed image items ─────────────────────────
  const handleAnalyzeAll = async () => {
    if (!apiKey.trim() || isRunning || isGlobalAnalyzing) return;
    const toAnalyze = items.filter(
      i => i.status === 'pending' && i.detectedType !== 'ringtone' && !i.isAnalyzing && !i.aiAnalyzed
    );
    if (toAnalyze.length === 0) return;

    setIsGlobalAnalyzing(true);

    // Mark all as analyzing
    const ids = new Set(toAnalyze.map(i => i.id));
    setItems(prev => prev.map(i => ids.has(i.id) ? { ...i, isAnalyzing: true } : i));

    // Run sequentially so we don't hammer the API
    for (const item of toAnalyze) {
      try {
        const base64 = await fileToBase64DataUrl(item.file);
        const result = await analyzeWallpaperImage(base64, apiKey);
        setItems(prev => prev.map(i => {
          if (i.id !== item.id) return i;
          return {
            ...i,
            title: result?.title || i.title,
            category: result?.category || i.category,
            isAnalyzing: false,
            aiAnalyzed: !!(result?.title || result?.category),
          };
        }));
      } catch {
        // On failure for one image, clear its loading state and continue
        setItems(prev => prev.map(i => i.id === item.id ? { ...i, isAnalyzing: false } : i));
      }
    }

    setIsGlobalAnalyzing(false);
  };

  // ── Upload all pending items ──────────────────────────────────────────────
  const handleUploadAll = async () => {
    const pending = items.filter(i => i.status === 'pending');
    if (pending.length === 0) return;

    // Validate: all pending items must have Title, Price, and Category
    const missingTitle = pending.find(i => !i.title.trim());
    if (missingTitle) { toast.error('All items must have a title before uploading'); return; }
    const missingCategory = pending.find(i => !i.category.trim());
    if (missingCategory) { toast.error('All items must have a category selected before uploading'); return; }
    const missingPrice = pending.find(i => i.price === '' || Number(i.price) < 1);
    if (missingPrice) { toast.error('All items must have a price of at least 1 SKR before uploading'); return; }

    const token = await getIdToken();
    if (!token || !user?.address) {
      toast.error('Authentication required');
      return;
    }

    setIsRunning(true);
    setDoneCount(0);
    setFailCount(0);
    setDupCount(0);

    // Build existing URL set for dedup
    const existingWallpapers = await getAllWallpapers();
    const existingUrls = new Set(existingWallpapers.map(w => w.imageUrl));

    let done = 0;
    let fail = 0;
    let dups = 0;

    for (let idx = 0; idx < pending.length; idx++) {
      const item = pending[idx];
      updateItem(item.id, { status: 'uploading' });

      try {
        // Normalize wallpaper images to the fixed 1400x3100 cover-crop, once, right before upload.
        // Live wallpapers (video) and ringtones (audio) pass through untouched.
        const fileToUpload = await processWallpaperImage(item.file);

        // Step 1: Upload file via admin upload-image endpoint
        const formData = new FormData();
        formData.append('file', fileToUpload);

        const uploadResponse = await fetch(getPoofAPIUrl('/api/upload-image'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Wallet-Address': user.address,
            ...getPoofAPIHeaders(),
          },
          body: formData,
        });

        const uploadJson = await uploadResponse.json() as {
          success: boolean;
          data?: { url: string };
          error?: { message: string };
        };

        if (!uploadJson.success || !uploadJson.data?.url) {
          throw new Error(uploadJson.error?.message || 'Upload failed');
        }

        const rawUrl = uploadJson.data.url.split('?')[0];
        const ct = item.detectedType;
        const isImage = ct === 'wallpaper';
        const finalImageUrl = isImage
          ? `${rawUrl}?w=2160&q=95&auto=format&fit=crop&crop=center`
          : rawUrl;
        const thumbnailUrl = isImage
          ? `${rawUrl}?w=600&q=85&auto=format&fit=crop&crop=center`
          : rawUrl;

        // Step 2: Dedup check
        if (existingUrls.has(finalImageUrl)) {
          updateItem(item.id, { status: 'duplicate' });
          dups++;
          setDupCount(dups);
          continue;
        }

        // Step 3: Save to collection — admin fields (status: published, ADMIN_ADDRESS)
        const wallpaperId = `wall-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 6)}`;
        const dbMediaType = MEDIA_TYPE_MAP[ct];

        const ok = await setWallpapers(wallpaperId, {
          title: (item.title || filenameToTitle(item.file.name)).trim(),
          imageUrl: finalImageUrl,
          thumbnailUrl,
          category: item.category || undefined,
          price: Math.round((item.price === '' ? 1 : Number(item.price)) * 1_000_000),
          uploadedBy: Address.publicKey(ADMIN_ADDRESS),
          createdAt: Time.Now,
          categorySetAt: Time.Now,
          downloadCount: 0,
          mediaType: dbMediaType,
          status: 'published',
          sizeBytes: fileToUpload.size,
        } as any);

        if (!ok) throw new Error('Failed to save to collection');

        existingUrls.add(finalImageUrl);
        updateItem(item.id, { status: 'done' });
        done++;
        setDoneCount(done);

        // Notify new category to parent
        if (item.category && onNewCategory) {
          if (ct === 'live') onNewCategory('live-wallpaper', item.category);
          else if (ct === 'ringtone') onNewCategory('ringtone', item.category);
          else onNewCategory('wallpaper', item.category);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        updateItem(item.id, { status: 'error', error: msg });
        fail++;
        setFailCount(fail);
      }
    }

    setIsRunning(false);

    if (done > 0) haptic.success();
    if (dups > 0) toast.warning(`${dups} file${dups !== 1 ? 's' : ''} skipped (already exist)`);
    if (fail > 0) { haptic.error(); toast.error(`${fail} file${fail !== 1 ? 's' : ''} failed to upload`); }

    // If all succeeded (no failures), close after short delay
    if (done > 0 && fail === 0) {
      setTimeout(() => {
        handleClose();
      }, 600);
    }
  };

  // ── Close / reset ─────────────────────────────────────────────────────────
  const handleClose = () => {
    if (isRunning) return;
    items.forEach(i => { try { URL.revokeObjectURL(i.localPreview); } catch { /* ignore */ } });
    setItems([]);
    setDoneCount(0);
    setFailCount(0);
    setDupCount(0);
    setCustomCategories([]);
    setAiSuggesting(false);
    setMediaType(initialMediaType ?? 'wallpaper');
    onClose();
  };

  const showAiLibraryButtons = mediaType === 'wallpaper';
  const pendingItems = items.filter(i => i.status === 'pending');
  const pendingCount = pendingItems.length;
  const pendingValid = pendingItems.every(i =>
    i.title.trim() && i.category.trim() && i.price !== '' && Number(i.price) >= 1
  );
  const uploadingIdx = items.findIndex(i => i.status === 'uploading');
  const totalFinished = items.filter(i => ['done', 'error', 'duplicate'].includes(i.status)).length;
  const progressNum = isRunning && items.length > 0
    ? Math.round((totalFinished / items.length) * 100)
    : 0;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 3000,
              background: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '16px',
            }}
            onClick={handleClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ type: 'spring', stiffness: 400, damping: 32 }}
              onClick={e => e.stopPropagation()}
              style={{
                width: '100%', maxWidth: 520,
                background: '#0a0a14',
                borderRadius: 20,
                border: '1px solid rgba(255,255,255,0.09)',
                overflow: 'hidden',
                maxHeight: 'calc(100vh - 32px)',
                overflowY: 'auto',
              }}
            >
              {/* ── SECTION 1: Header ── */}
              <div style={{ padding: '14px 16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <motion.button
                    whileHover={{ scale: 1.1, background: 'rgba(139,92,246,0.25)' }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleClose}
                    title="Go back"
                    disabled={isRunning}
                    style={{
                      width: 36, height: 36, flexShrink: 0,
                      borderRadius: 10,
                      background: 'rgba(139,92,246,0.12)',
                      border: '1px solid rgba(139,92,246,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: isRunning ? 'not-allowed' : 'pointer',
                      color: '#a78bfa',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <ArrowLeft size={18} />
                  </motion.button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 15, fontWeight: 700, color: '#a78bfa',
                      fontFamily: "'Syne', sans-serif", lineHeight: 1.1,
                      letterSpacing: '-0.01em',
                    }}>
                      Add Item
                    </div>
                    {items.length > 0 && (
                      <div style={{
                        fontSize: 11, color: 'rgba(200,200,210,0.5)',
                        fontFamily: "'DM Mono', monospace", marginTop: 2,
                      }}>
                        {items.length} file{items.length !== 1 ? 's' : ''} queued
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleClose}
                      disabled={isRunning}
                      style={{
                        width: 28, height: 28, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: isRunning ? 'not-allowed' : 'pointer',
                        color: 'rgba(240,240,245,0.6)',
                      }}
                    >
                      <X size={14} />
                    </motion.button>
                  </div>
                </div>

                <div style={{
                  height: 1,
                  background: 'rgba(255,255,255,0.08)',
                  marginTop: 10, marginBottom: 0,
                }} />
              </div>

              {/* ── SECTION 3: Compact AI API key row (full width) — AI WINDOW, KEEP AS-IS ── */}
              <div style={{ padding: '8px 16px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: 'rgba(167,139,250,0.55)',
                    fontFamily: "'Syne', sans-serif",
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    AI Key
                  </span>
                  <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                    <input
                      value={showApiKey ? apiKey : (apiKey ? '••••••••••••••' : '')}
                      onChange={e => showApiKey ? handleApiKeyChange(e.target.value) : undefined}
                      readOnly={!showApiKey}
                      placeholder={apiKeyLoaded ? 'sk-... / r8_... / sk_...' : 'Loading...'}
                      disabled={!apiKeyLoaded}
                      style={{
                        width: '100%', padding: '5px 8px',
                        paddingRight: (detectedProvider && showApiKey) ? '72px' : '8px',
                        borderRadius: 7, border: '1px solid rgba(139,92,246,0.2)',
                        background: 'rgba(255,255,255,0.03)', color: '#f0f0f5',
                        fontSize: 10, outline: 'none', boxSizing: 'border-box',
                        fontFamily: showApiKey ? "'DM Mono', monospace" : 'monospace',
                        opacity: apiKeyLoaded ? 1 : 0.5,
                        letterSpacing: showApiKey ? 'normal' : '0.05em',
                      }}
                    />
                    {detectedProvider && showApiKey && (
                      <div style={{
                        position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                        padding: '1px 5px', borderRadius: 4,
                        background: detectedProvider === 'Anthropic' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                        border: detectedProvider === 'Anthropic' ? '1px solid rgba(239,68,68,0.4)' : '1px solid rgba(34,197,94,0.3)',
                        fontSize: 8, fontWeight: 700,
                        color: detectedProvider === 'Anthropic' ? '#f87171' : '#4ade80',
                        fontFamily: "'Syne', sans-serif",
                        whiteSpace: 'nowrap',
                      }}>
                        {detectedProvider === 'Anthropic' ? '✗ N/A' : `✓ ${detectedProvider}`}
                      </div>
                    )}
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowApiKey(v => !v)}
                    title={showApiKey ? 'Hide API key' : 'Show API key'}
                    style={{
                      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                      background: showApiKey ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.05)',
                      border: showApiKey ? '1px solid rgba(139,92,246,0.4)' : '1px solid rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      color: showApiKey ? '#a78bfa' : 'rgba(240,240,245,0.4)',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {showApiKey ? <EyeOff size={11} /> : <Eye size={11} />}
                  </motion.button>
                  <motion.button
                    whileHover={(!apiKey || deletingKey) ? {} : { scale: 1.1 }}
                    whileTap={(!apiKey || deletingKey) ? {} : { scale: 0.9 }}
                    onClick={apiKey && !deletingKey ? handleDeleteApiKey : undefined}
                    title="Delete saved API key"
                    disabled={!apiKey || deletingKey}
                    style={{
                      width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                      background: 'rgba(239,68,68,0.08)',
                      border: '1px solid rgba(239,68,68,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: (!apiKey || deletingKey) ? 'not-allowed' : 'pointer',
                      color: (!apiKey || deletingKey) ? 'rgba(239,68,68,0.25)' : 'rgba(248,113,113,0.7)',
                      transition: 'all 0.15s ease',
                      opacity: (!apiKey || deletingKey) ? 0.5 : 1,
                    }}
                  >
                    {deletingKey
                      ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Trash2 size={11} />
                    }
                  </motion.button>
                </div>
              </div>

              {/* ── SECTION 3b: AI / Library pill buttons — AI WINDOW, KEEP AS-IS ── */}
              <div style={{ padding: '6px 16px 0', display: 'flex', alignItems: 'center', gap: 5 }}>
                {/* AI — only for Wallpaper */}
                {showAiLibraryButtons && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowGenerate(true)}
                    title="AI generate wallpapers"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 9px', borderRadius: 12,
                      background: 'rgba(139,92,246,0.15)',
                      border: '1px solid rgba(139,92,246,0.3)',
                      color: '#a78bfa',
                      fontSize: 10, fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: "'Syne', sans-serif",
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Sparkles size={10} />
                    AI
                  </motion.button>
                )}

                {/* Library — only for Wallpaper */}
                {showAiLibraryButtons && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowLibrary(true)}
                    title="Fetch from Unsplash library"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 9px', borderRadius: 12,
                      background: 'rgba(34,197,94,0.15)',
                      border: '1px solid rgba(34,197,94,0.3)',
                      color: '#4ade80',
                      fontSize: 10, fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: "'Syne', sans-serif",
                      transition: 'all 0.15s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Globe size={10} />
                    Library
                  </motion.button>
                )}
              </div>

              {/* ── SECTION 5: User-style upload area ── */}
              <div style={{ padding: '8px 16px 16px' }}>
                <div style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 14,
                  padding: '10px 12px 12px',
                }}>
                  {/* Drop-zone / choose-file button */}
                  <motion.button
                    whileHover={isRunning ? {} : { scale: 1.01 }}
                    whileTap={isRunning ? {} : { scale: 0.99 }}
                    onClick={() => !isRunning && fileInputRef.current?.click()}
                    disabled={isRunning}
                    style={{
                      width: '100%', padding: '12px 14px',
                      borderRadius: 10,
                      background: items.length > 0 ? 'rgba(139,92,246,0.08)' : 'rgba(255,255,255,0.04)',
                      border: items.length > 0 ? '1px solid rgba(167,139,250,0.35)' : '2px dashed rgba(139,92,246,0.3)',
                      color: items.length > 0 ? '#a78bfa' : 'rgba(240,240,245,0.4)',
                      cursor: isRunning ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', gap: 10,
                      transition: 'all 0.14s ease',
                      textAlign: 'left',
                      marginBottom: 10,
                    }}
                  >
                    <Upload size={15} style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace" }}>
                      {items.length > 0 ? `${items.length} file${items.length !== 1 ? 's' : ''} selected — click to add more` : 'Choose files… (multi-select supported)'}
                    </span>
                  </motion.button>

                  {/* Hidden multi-file input — accepts ALL types for auto-detect */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ALL_ACCEPT_ATTR}
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />

                  {/* Global AI button — analyze all unanalyzed image items at once */}
                  {items.length > 0 && (() => {
                    const analyzableItems = items.filter(
                      i => i.status === 'pending' && i.detectedType !== 'ringtone' && !i.isAnalyzing && !i.aiAnalyzed
                    );
                    const anyAnalyzing = items.some(i => i.isAnalyzing);
                    const globalBtnDisabled = isRunning || !apiKey.trim() || anyAnalyzing || analyzableItems.length === 0;
                    return (
                      <div style={{ marginBottom: 10 }}>
                        <motion.button
                          whileHover={globalBtnDisabled ? {} : { scale: 1.02, boxShadow: '0 0 12px rgba(234,179,8,0.25)' }}
                          whileTap={globalBtnDisabled ? {} : { scale: 0.98 }}
                          onClick={globalBtnDisabled ? undefined : handleAnalyzeAll}
                          disabled={globalBtnDisabled}
                          title={
                            !apiKey.trim()
                              ? 'Добавьте AI-ключ выше, чтобы использовать эту функцию'
                              : analyzableItems.length === 0 && !anyAnalyzing
                              ? 'Все изображения уже проанализированы'
                              : `Определить название и категорию для ${analyzableItems.length} изображений`
                          }
                          style={{
                            width: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                            padding: '8px 12px', borderRadius: 10,
                            background: globalBtnDisabled
                              ? 'rgba(20,15,40,0.5)'
                              : 'rgba(139,92,246,0.18)',
                            border: globalBtnDisabled
                              ? '1px solid rgba(139,92,246,0.15)'
                              : '1px solid rgba(234,179,8,0.5)',
                            color: globalBtnDisabled ? 'rgba(240,240,245,0.3)' : '#f0d060',
                            fontSize: 12, fontWeight: 700,
                            cursor: globalBtnDisabled ? 'not-allowed' : 'pointer',
                            fontFamily: "'Syne', sans-serif",
                            transition: 'all 0.15s ease',
                            opacity: globalBtnDisabled ? 0.5 : 1,
                          }}
                        >
                          {anyAnalyzing ? (
                            <>
                              <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: '#a78bfa' }} />
                              <span>AI анализирует...</span>
                            </>
                          ) : (
                            <>
                              <span style={{ color: globalBtnDisabled ? 'rgba(234,179,8,0.3)' : '#FFD700', fontSize: 14 }}>✦</span>
                              <span>Определить все{analyzableItems.length > 0 ? ` (${analyzableItems.length})` : ''}</span>
                            </>
                          )}
                        </motion.button>
                      </div>
                    );
                  })()}

                  {/* Upload queue */}
                  {items.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                      <AnimatePresence initial={false}>
                        {items.map((item, idx) => (
                          <UploadItemRow
                            key={item.id}
                            item={item}
                            index={idx}
                            isRunning={isRunning}
                            apiKey={apiKey}
                            categoriesForType={getCategoriesForType}
                            customCategories={customCategories}
                            onUpdate={patch => updateItem(item.id, patch)}
                            onRemove={() => removeItem(item.id)}
                            onAddCategory={(cat, ct) => {
                              setCustomCategories(prev => prev.includes(cat) ? prev : [...prev, cat]);
                              if (onNewCategory) {
                                const mt: 'wallpaper' | 'live-wallpaper' | 'ringtone' =
                                  ct === 'live' ? 'live-wallpaper' : ct === 'ringtone' ? 'ringtone' : 'wallpaper';
                                onNewCategory(mt, cat);
                              }
                            }}
                          />
                        ))}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Progress bar while running */}
                  {isRunning && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: 11, color: 'rgba(240,240,245,0.5)', fontFamily: "'DM Mono', monospace" }}>
                          Uploading {uploadingIdx >= 0 ? uploadingIdx + 1 : totalFinished + 1} / {items.length}
                        </span>
                        <span style={{ fontSize: 11, color: 'rgba(167,139,250,0.8)', fontFamily: "'DM Mono', monospace" }}>
                          {progressNum}%
                        </span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                        <motion.div
                          animate={{ width: `${progressNum}%` }}
                          transition={{ duration: 0.4 }}
                          style={{ height: '100%', background: 'linear-gradient(90deg,#7c3aed,#a78bfa)', borderRadius: 2 }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Bottom row: Upload button + Cancel */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <motion.button
                      whileHover={(isRunning || pendingCount === 0 || !pendingValid) ? {} : { scale: 1.02, boxShadow: '0 0 18px rgba(139,92,246,0.4)' }}
                      whileTap={(isRunning || pendingCount === 0 || !pendingValid) ? {} : { scale: 0.98 }}
                      onClick={handleUploadAll}
                      disabled={isRunning || pendingCount === 0 || !pendingValid}
                      style={{
                        flex: 1,
                        padding: '8px 12px', borderRadius: 10,
                        background: (isRunning || pendingCount === 0 || !pendingValid) ? 'rgba(139,92,246,0.4)' : 'rgba(139,92,246,0.85)',
                        border: '1px solid rgba(167,139,250,0.45)',
                        color: '#fff', fontSize: 13, fontWeight: 700,
                        cursor: (isRunning || pendingCount === 0 || !pendingValid) ? 'not-allowed' : 'pointer',
                        fontFamily: "'Syne', sans-serif",
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                        opacity: (isRunning || pendingCount === 0 || !pendingValid) ? 0.65 : 1,
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {isRunning ? (
                        <>
                          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Plus size={14} />
                          {pendingCount > 0 ? `Add ${pendingCount} to Catalog` : 'Add to Catalog'}
                        </>
                      )}
                    </motion.button>

                    <button
                      onClick={handleClose}
                      disabled={isRunning}
                      style={{
                        background: 'none', border: 'none', padding: '8px 6px',
                        color: 'rgba(240,240,245,0.4)',
                        fontSize: 12, fontWeight: 600,
                        fontFamily: "'Syne', sans-serif",
                        cursor: isRunning ? 'not-allowed' : 'pointer',
                        transition: 'color 0.15s ease',
                        flexShrink: 0,
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(240,240,245,0.7)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(240,240,245,0.4)'; }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sub-modals — AI WINDOW, KEEP AS-IS */}
      <AdminBulkUpload
        isOpen={showBulk}
        onClose={() => setShowBulk(false)}
        onNavigateToAdmin={() => setShowBulk(false)}
        existingCategories={existingCategories}
        existingLiveWallpaperCategories={existingLiveWallpaperCategories}
        existingRingtoneCategories={existingRingtoneCategories}
        mediaType={mediaType}
        onNewCategory={onNewCategory}
      />
      <AdminGenerateWallpaper
        isOpen={showGenerate}
        onClose={() => setShowGenerate(false)}
        onNavigateToAdmin={() => setShowGenerate(false)}
        existingCategories={existingCategories}
        existingLiveWallpaperCategories={existingLiveWallpaperCategories}
        existingRingtoneCategories={existingRingtoneCategories}
        initialMediaType={mediaType}
        onNewCategory={onNewCategory}
      />
      <AdminFetchWallpapers
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onNavigateToAdmin={() => setShowLibrary(false)}
        existingCategories={existingCategories}
      />
    </>
  );
};

// ── Per-file row component ─────────────────────────────────────────────────

interface UploadItemRowProps {
  item: UploadItem;
  index: number;
  isRunning: boolean;
  apiKey: string;
  categoriesForType: (ct: ContentType) => string[];
  customCategories: string[];
  onUpdate: (patch: Partial<UploadItem>) => void;
  onRemove: () => void;
  onAddCategory: (cat: string, ct: ContentType) => void;
}

const UploadItemRow: React.FC<UploadItemRowProps> = ({
  item, isRunning, apiKey, categoriesForType, onUpdate, onRemove,
}) => {
  const [aiLoading, setAiLoading] = useState(false);
  const anyAiActive = aiLoading || !!item.isAnalyzing;
  const isEditable = item.status === 'pending' && !isRunning && !anyAiActive;
  const cats = categoriesForType(item.detectedType);
  const badge = CONTENT_TYPE_BADGE[item.detectedType];
  const canUseAi = !!apiKey.trim() && item.status === 'pending' && !isRunning && !anyAiActive && item.detectedType !== 'ringtone';

  const statusColor = {
    pending: 'rgba(255,255,255,0.2)',
    uploading: '#a78bfa',
    done: '#4ade80',
    error: '#f87171',
    duplicate: '#fbbf24',
  }[item.status];

  const rowBg = item.status === 'error'
    ? 'rgba(239,68,68,0.06)'
    : item.status === 'done'
    ? 'rgba(34,197,94,0.05)'
    : item.status === 'duplicate'
    ? 'rgba(234,179,8,0.06)'
    : 'rgba(255,255,255,0.03)';

  const rowBorder = item.status === 'error'
    ? '1px solid rgba(239,68,68,0.2)'
    : item.status === 'done'
    ? '1px solid rgba(34,197,94,0.15)'
    : item.status === 'duplicate'
    ? '1px solid rgba(234,179,8,0.2)'
    : '1px solid rgba(255,255,255,0.07)';

  const handleAiGenerate = async () => {
    if (!canUseAi || aiLoading) return;
    if (!apiKey.trim()) {
      toast.error('Add an AI API key first');
      return;
    }
    setAiLoading(true);
    try {
      const base64 = await fileToBase64DataUrl(item.file);
      const result = await analyzeWallpaperImage(base64, apiKey);
      if (result) {
        onUpdate({
          title: result.title || item.title,
          category: result.category || item.category,
          aiAnalyzed: !!(result.title || result.category),
        });
      } else {
        toast.error('AI returned no result');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'AI generation failed';
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      style={{
        display: 'flex', gap: 10, alignItems: 'flex-start',
        padding: '10px 10px 10px',
        borderRadius: 10,
        background: rowBg, border: rowBorder,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Thumbnail */}
      <div style={{
        width: 58, height: 58, borderRadius: 7, flexShrink: 0,
        overflow: 'hidden', background: 'rgba(255,255,255,0.05)',
        position: 'relative',
      }}>
        {item.detectedType === 'live' ? (
          <video src={item.localPreview} muted playsInline
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : item.detectedType === 'ringtone' ? (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(167,139,250,0.1)',
            fontSize: 20,
          }}>
            🎵
          </div>
        ) : (
          <img src={item.localPreview} alt={item.title}
            loading="lazy" decoding="async"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {/* Status overlay */}
        {item.status === 'uploading' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Loader2 size={16} color="#a78bfa" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        {item.status === 'done' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckCircle2 size={18} color="#4ade80" />
          </div>
        )}
        {item.status === 'duplicate' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{
              padding: '2px 6px', borderRadius: 4,
              background: 'rgba(234,179,8,0.85)', color: '#000',
              fontSize: 8, fontWeight: 800, fontFamily: "'Syne', sans-serif",
              textTransform: 'uppercase',
            }}>DUP</span>
          </div>
        )}
        {item.status === 'error' && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertCircle size={18} color="#f87171" />
          </div>
        )}
      </div>

      {/* Fields — two-row layout */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {/* Row 1: Title with label row (type badge + TITLE label + ✦ AI button) */}
        <div>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 3,
          }}>
            {/* Left: type badge + TITLE label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 6px', borderRadius: 20, flexShrink: 0,
                background: 'rgba(139,92,246,0.12)',
                border: '1px solid rgba(167,139,250,0.3)',
                color: '#c4b5fd', fontSize: 9, fontWeight: 700,
                fontFamily: "'Syne', sans-serif",
                letterSpacing: '0.04em',
              }}>
                <span style={{ fontSize: 10 }}>{badge.emoji}</span>
                {badge.label}
              </div>
              <span style={{
                fontSize: 9, fontWeight: 700,
                color: 'rgba(167,139,250,0.7)',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                fontFamily: "'Syne', sans-serif",
              }}>
                Title
              </span>
            </div>
            {/* Right: ✦ AI button or analyzing indicator (shown when pending and not a ringtone) */}
            {item.status === 'pending' && !isRunning && item.detectedType !== 'ringtone' && (
              anyAiActive ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 7px' }}>
                  <Loader2 size={9} color="#a78bfa" style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'Syne', sans-serif", color: 'rgba(167,139,250,0.7)' }}>AI...</span>
                </div>
              ) : (
                <motion.button
                  whileHover={canUseAi ? { scale: 1.08, boxShadow: '0 0 8px rgba(139,92,246,0.35)' } : {}}
                  whileTap={canUseAi ? { scale: 0.92 } : {}}
                  onClick={handleAiGenerate}
                  disabled={!canUseAi}
                  title={!apiKey.trim() ? 'Add an AI API key to use this feature' : item.aiAnalyzed ? 'Проанализировано AI — нажмите для повторного анализа' : 'Generate title & category with AI'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 3,
                    padding: '2px 7px', borderRadius: 7, flexShrink: 0,
                    background: canUseAi ? 'rgba(20,15,40,0.8)' : 'rgba(20,15,40,0.5)',
                    border: canUseAi ? '1px solid rgba(139,92,246,0.35)' : '1px solid rgba(139,92,246,0.15)',
                    cursor: !canUseAi ? 'not-allowed' : 'pointer',
                    color: canUseAi ? 'rgba(240,240,245,0.85)' : 'rgba(240,240,245,0.3)',
                    transition: 'all 0.15s ease',
                    opacity: canUseAi ? 1 : 0.5,
                  }}
                >
                  <span style={{ color: canUseAi ? '#FFD700' : 'rgba(234,179,8,0.3)', fontSize: 10, lineHeight: 1 }}>
                    {item.aiAnalyzed ? '✦' : '✦'}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, fontFamily: "'Syne', sans-serif", letterSpacing: '0.02em' }}>AI</span>
                </motion.button>
              )
            )}
          </div>
          <input
            value={item.title}
            onChange={e => onUpdate({ title: e.target.value })}
            disabled={!isEditable}
            placeholder="Title"
            style={{
              width: '100%',
              padding: '4px 8px',
              borderRadius: 7, border: '1px solid rgba(255,255,255,0.1)',
              background: isEditable ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
              color: '#f0f0f5', fontSize: 11, outline: 'none',
              fontFamily: "'Syne', sans-serif",
              opacity: isEditable ? 1 : 0.65,
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Row 2: Category + Price + Delete */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {/* Category dropdown */}
          <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
            <select
              value={cats.includes(item.category) ? item.category : (item.category ? '__custom__' : '')}
              onChange={e => {
                const v = e.target.value;
                if (v !== '__custom__' && v !== '' && v !== '__new_cat__') onUpdate({ category: v });
              }}
              disabled={!isEditable}
              style={{
                width: '100%', padding: '4px 18px 4px 7px',
                borderRadius: 7,
                border: item.category ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(248,113,113,0.45)',
                background: isEditable ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
                color: item.category ? '#f0f0f5' : 'rgba(248,113,113,0.8)',
                fontSize: 10, outline: 'none', cursor: isEditable ? 'pointer' : 'default',
                fontFamily: "'Syne', sans-serif",
                appearance: 'none', WebkitAppearance: 'none',
                opacity: isEditable ? 1 : 0.65,
              } as React.CSSProperties}
            >
              <option value="" style={{ background: '#0a0a14', color: 'rgba(248,113,113,0.8)' }}>— categ —</option>
              {cats.map(cat => (
                <option key={cat} value={cat} style={{ background: '#0a0a14' }}>{cat}</option>
              ))}
              {item.category && !cats.includes(item.category) && (
                <option value="__custom__" style={{ background: '#0a0a14', color: '#a78bfa' }}>
                  {item.category}
                </option>
              )}
              <option value="__new_cat__" style={{ background: '#0a0a14' }}>+ New…</option>
            </select>
            <ChevronDown size={9} style={{
              position: 'absolute', right: 5, top: '50%', transform: 'translateY(-50%)',
              color: 'rgba(240,240,245,0.3)', pointerEvents: 'none',
            }} />
          </div>

          {/* Price */}
          <input
            type="number" min="1" step="1"
            value={item.price}
            disabled={!isEditable}
            onChange={e => {
              if (!isEditable) return;
              const raw = e.target.value;
              if (raw === '') { onUpdate({ price: '' }); return; }
              const v = parseFloat(raw);
              if (!isNaN(v) && v >= 0) onUpdate({ price: v });
            }}
            placeholder="SKR"
            style={{
              width: 46, padding: '4px 4px', borderRadius: 7, flexShrink: 0,
              background: (item.price === '' || Number(item.price) < 1) ? 'rgba(239,68,68,0.08)' : 'rgba(139,92,246,0.10)',
              border: (item.price === '' || Number(item.price) < 1) ? '1px solid rgba(248,113,113,0.45)' : '1px solid rgba(139,92,246,0.28)',
              color: (item.price === '' || Number(item.price) < 1) ? 'rgba(248,113,113,0.9)' : '#f0f0f5',
              fontSize: 10, fontWeight: 700,
              fontFamily: "'DM Mono', monospace",
              opacity: isEditable ? 1 : 0.65,
              textAlign: 'center', outline: 'none', boxSizing: 'border-box',
              WebkitAppearance: 'none',
            } as React.CSSProperties}
          />

          {/* Status indicator / remove button */}
          <div style={{ flexShrink: 0, width: 26, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {item.status === 'pending' && !isRunning && (
              <motion.button
                whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                onClick={onRemove}
                style={{
                  width: 24, height: 24, borderRadius: 7,
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'rgba(248,113,113,0.7)',
                }}
              >
                <Trash2 size={10} />
              </motion.button>
            )}
            {item.status === 'uploading' && (
              <Loader2 size={13} color={statusColor} style={{ animation: 'spin 1s linear infinite' }} />
            )}
            {item.status === 'done' && <CheckCircle2 size={15} color={statusColor} />}
            {item.status === 'duplicate' && (
              <span title="Already exists" style={{
                fontSize: 8, fontWeight: 800, color: '#fbbf24',
                fontFamily: "'Syne', sans-serif",
                background: 'rgba(234,179,8,0.15)',
                border: '1px solid rgba(234,179,8,0.3)',
                borderRadius: 4, padding: '2px 4px',
                whiteSpace: 'nowrap', lineHeight: 1,
              }}>DUP</span>
            )}
            {item.status === 'error' && (
              <span title={item.error ?? ''} style={{ display: 'flex' }}>
                <AlertCircle size={15} color={statusColor} />
              </span>
            )}
          </div>
        </div>

        {/* Error message */}
        {item.status === 'error' && item.error && (
          <div style={{
            fontSize: 10, color: '#f87171',
            fontFamily: "'DM Mono', monospace",
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {item.error}
          </div>
        )}
      </div>
    </motion.div>
  );
};
